import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { useSegmentBuilderStore } from '@/stores/segmentBuilder.store';

interface EstimationSectionProps {
  onRefresh: () => void;
}

// Helper to format large numbers
function formatNumber(num: number | null): string {
  if (num === null) return 'N/A';
  return num.toLocaleString();
}

export function EstimationSection({ onRefresh }: EstimationSectionProps) {
  // Get state directly from the store
  const estimateCount = useSegmentBuilderStore((state) => state.estimateCount);
  const isEstimating = useSegmentBuilderStore((state) => state.isEstimating);
  const estimateError = useSegmentBuilderStore((state) => state.estimateError);

  return (
    <Card className="relative">
      {' '}
      {/* Add relative positioning for overlay */}
      <CardHeader>
        <CardTitle className="text-lg">Estimated Size</CardTitle>
        <CardDescription>An estimate of users matching the criteria.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Loading State Overlay */}
        {isEstimating && (
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-10 rounded-lg">
            <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="sr-only">Calculating estimate...</span>
          </div>
        )}

        {/* Error State */}
        {estimateError && !isEstimating && (
          <div className="flex items-center space-x-3 text-destructive bg-destructive/10 p-3 rounded-md">
            <AlertCircle className="h-5 w-5 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-medium">Error calculating estimate</p>
              <p className="text-xs">{estimateError}</p>
            </div>
          </div>
        )}

        {/* Estimate Display */}
        {!estimateError && (
          <div className="text-4xl font-bold tracking-tight">
            {estimateCount === null ? (
              <span className="text-muted-foreground">N/A</span>
            ) : (
              formatNumber(estimateCount)
            )}
          </div>
        )}

        {/* Refresh Button */}
        <Button
          variant="outline"
          size="sm"
          onClick={onRefresh}
          disabled={isEstimating}
          className="w-full"
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${isEstimating ? 'animate-spin' : ''}`} />
          {isEstimating ? 'Calculating...' : 'Refresh Estimate'}
        </Button>
      </CardContent>
    </Card>
  );
}
